import React, { useMemo, useState } from 'react';
import { useCookieConsent } from '../../contexts/CookieConsentContext';

type CookieConsentValue = 'essential' | 'analytics' | 'marketing';

const cookieCategories: Array<{ value: CookieConsentValue; labelKey: string; descriptionKey: string; disabled: boolean }> = [
  {
    value: 'essential',
    labelKey: 'cookieConsent.categories.essential',
    descriptionKey: 'cookieConsent.categories.essentialDescription',
    disabled: true,
  },
  {
    value: 'analytics',
    labelKey: 'cookieConsent.categories.analytics',
    descriptionKey: 'cookieConsent.categories.analyticsDescription',
    disabled: false,
  },
  {
    value: 'marketing',
    labelKey: 'cookieConsent.categories.marketing',
    descriptionKey: 'cookieConsent.categories.marketingDescription',
    disabled: false,
  },
];

const CookieConsentBanner: React.FC = () => {
  const { display, isBannerVisible, acceptAll, rejectNonEssential, savePreferences } = useCookieConsent();
  const [isHidden, setIsHidden] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [preferences, setPreferences] = useState({ analytics: false, marketing: false });

  const isVisible = isBannerVisible && !isHidden;

  const consentSummary = useMemo(() => {
    return preferences.analytics || preferences.marketing
      ? display.summaryAny
      : display.summaryEssential;
  }, [preferences, display]);

  const onSavePreferences = () => {
    savePreferences(preferences);
    setIsHidden(true);
  };

  if (!isVisible) {
    return null;
  }

  return (
    <section
      className="cookie-banner"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cookie-banner-title"
      aria-describedby="cookie-banner-description"
    >
      <div className="cookie-banner__card">
        <div className="cookie-banner__header">
          <div>
            <p id="cookie-banner-title" className="cookie-banner__title">
              {display.title}
            </p>
            <p id="cookie-banner-description" className="cookie-banner__copy">
              {display.description}
            </p>
          </div>
          <button
            type="button"
            className="cookie-banner__close"
            onClick={() => {
              rejectNonEssential();
              setIsHidden(true);
            }}
            aria-label={display.closeAriaLabel}
          >
            ×
          </button>
        </div>

        <div className="cookie-banner__actions">
          <button type="button" className="btn btn-primary" onClick={() => { acceptAll(); setIsHidden(true); }}>
            {display.acceptAll}
          </button>
          <button type="button" className="btn btn-secondary" onClick={() => { rejectNonEssential(); setIsHidden(true); }}>
            {display.rejectNonEssential}
          </button>
          <button type="button" className="btn btn-ghost" onClick={() => setIsExpanded((current) => !current)}>
            {display.cookieSettings}
          </button>
        </div>

        <div className="cookie-banner__note">{consentSummary}</div>

        <div className="cookie-banner__footer">
          <button type="button" className="cookie-banner__learn-more" onClick={() => setIsExpanded((current) => !current)}>
            {display.learnMore}
          </button>
          <span>{display.essentialActive}</span>
        </div>

        {isExpanded ? (
          <div className="cookie-banner__details">
            <div className="cookie-banner__details-copy">
              <p className="cookie-banner__details-heading">{display.detailsHeading}</p>
              <p>
                {display.detailsCopy}
              </p>
            </div>
            <form className="cookie-banner__preferences" onSubmit={(event) => { event.preventDefault(); onSavePreferences(); }}>
              {cookieCategories.map((category) => (
                <label key={category.value} className="cookie-banner__preference-row">
                  <div className="cookie-banner__preference-label">
                    <span>{display.categories[category.value].label}</span>
                    <p>{display.categories[category.value].description}</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={category.value === 'essential' ? true : preferences[category.value]}
                    disabled={category.disabled}
                    onChange={(event) => {
                      if (category.disabled) return;
                      setPreferences((current) => ({
                        ...current,
                        [category.value]: event.target.checked,
                      }));
                    }}
                  />
                </label>
              ))}
              <div className="cookie-banner__preferences-actions">
                <button type="submit" className="btn btn-primary">
                  {display.savePreferences}
                </button>
                <button type="button" className="btn btn-ghost" onClick={() => setIsExpanded(false)}>
                  {display.back}
                </button>
              </div>
            </form>
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default CookieConsentBanner;
